// Avance de un proyecto (D96): horas usadas contra las estimadas (en total y por fase), valor consumido (horas x tarifa)
// contra el vendido, atraso y riesgo. Funciones puras: las usan la lista, la ficha, el Resumen de Ventas y la campana.
import type { Fase, Proyecto, RegistroHoras } from '../api/proyectos';

const DIA = 86_400_000;
const BOGOTA = -5 * 3_600_000; // Colombia no cambia de hora
/** Dia de calendario en Bogota (para contar dias hasta la entrega sin fracciones). */
const diaNum = (t: number) => Math.floor((t + BOGOTA) / DIA);

/** Plantilla para un proyecto nuevo (se puede editar despues): nombre y horas estimadas. */
export const PLANTILLA_FASES: [string, number][] = [
  ['Levantamiento', 4],
  ['Instalación', 8],
  ['Configuración', 8],
  ['Capacitación', 4],
  ['Cierre y entrega', 2],
];

export type Riesgo = 'ok' | 'alto' | 'pasado';
export type Avance = {
  /** Minutos registrados en el proyecto. */
  usados: number;
  /** Minutos estimados: las horas presupuestadas del proyecto, o la suma de sus fases. */
  estimados: number;
  pct: number | null;
  valor: number | null;
  valorConsumido: number | null;
  porFase: { fase: Fase; usados: number; estimados: number }[];
  sinFase: number;
  porAprobar: number;
  diasEntrega: number | null;
  atrasado: boolean;
  riesgo: Riesgo;
};

export function avanceDe(p: Proyecto, fases: Fase[], regs: RegistroHoras[], ahora = Date.now()): Avance {
  const suyos = regs.filter((r) => r.proyectoId === p.id);
  const suyas = fases.filter((f) => f.proyectoId === p.id).sort((a, b) => a.posicion - b.posicion);
  const usados = suyos.reduce((s, r) => s + r.minutos, 0);
  const deFases = suyas.reduce((s, f) => s + (f.horasEstimadas ?? 0) * 60, 0);
  const estimados = p.horasPresupuestadas ? p.horasPresupuestadas * 60 : deFases;
  const pct = estimados ? Math.round((100 * usados) / estimados) : null;
  const ids = new Set(suyas.map((f) => f.id));
  const terminado = p.estado === 'TERMINADO';
  const diasEntrega = p.entrega ? diaNum(Date.parse(p.entrega)) - diaNum(ahora) : null;
  const atrasado = !terminado && p.estado !== 'PROPUESTO' && diasEntrega != null && diasEntrega < 0;
  const riesgo: Riesgo = terminado || pct == null ? 'ok' : pct > 100 ? 'pasado' : pct >= 80 ? 'alto' : 'ok';
  return {
    usados,
    estimados,
    pct,
    valor: p.valor,
    valorConsumido: p.tarifaHora ? Math.round((usados / 60) * p.tarifaHora) : null,
    porFase: suyas.map((f) => ({ fase: f, usados: suyos.filter((r) => r.faseId === f.id).reduce((s, r) => s + r.minutos, 0), estimados: (f.horasEstimadas ?? 0) * 60 })),
    sinFase: suyos.filter((r) => !r.faseId || !ids.has(r.faseId)).reduce((s, r) => s + r.minutos, 0),
    porAprobar: suyos.filter((r) => r.aprobacion === 'PENDIENTE').reduce((s, r) => s + r.minutos, 0),
    diasEntrega,
    atrasado,
    riesgo,
  };
}

/** Avisos del proyecto, del mas grave al menos (vacio si va bien). */
export function alertasProyecto(a: Avance): { texto: string; tono: 'bad' | 'warn' }[] {
  const r: { texto: string; tono: 'bad' | 'warn' }[] = [];
  if (a.riesgo === 'pasado') r.push({ texto: `Va en ${a.pct} % de las horas estimadas`, tono: 'bad' });
  if (a.atrasado) r.push({ texto: `Atrasado ${-a.diasEntrega!} ${a.diasEntrega === -1 ? 'día' : 'días'}`, tono: 'bad' });
  if (a.riesgo === 'alto') r.push({ texto: `Va en ${a.pct} % de las horas estimadas`, tono: 'warn' });
  if (a.valor && a.valorConsumido != null && a.valorConsumido > a.valor) r.push({ texto: 'Ya consumió más del valor vendido', tono: 'bad' });
  if (!a.atrasado && a.diasEntrega != null && a.diasEntrega >= 0 && a.diasEntrega <= 7) r.push({ texto: a.diasEntrega ? `Entrega en ${a.diasEntrega} ${a.diasEntrega === 1 ? 'día' : 'días'}` : 'Entrega hoy', tono: 'warn' });
  return r;
}

export type GrupoProyecto = 'atender' | 'activos' | 'propuestos' | 'cerrados';
export const grupoProyecto = (p: Proyecto, a: Avance): GrupoProyecto =>
  p.estado === 'TERMINADO' || p.estado === 'PAUSADO' ? 'cerrados' : p.estado === 'PROPUESTO' ? 'propuestos' : a.riesgo !== 'ok' || a.atrasado ? 'atender' : 'activos';
